import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Typography, { TypographyProps } from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';

import setDiffsHelper from '#utils/set-diffs-helper';
import type { Diff } from '#utils/order-diff-utils';
import makeid from '#utils/make-id';
import {
  DATE_FORMAT_STRING,
  formatCurrency,
  calcOrderTotalPrice,
  formatDate,
} from '#common/utils';
import URLSearchParamsEx from '#utils/url-ex';
import {
  FULFILLMENT_DATA,
  FULFILLMENT_STATUS,
} from '#common/constants';
import {
  useGetInventoryOrderMutation,
  useUpdateInventoryOrderMutation,
} from '#services/inventory';
import {
  useReceiveAllBarcodeItemsMutation,
} from '#services/order';
import EnhancedTable, { EnhancedTableData } from '#components/EnhancedTable';
import OrderItemDetails, { OrderItemDetailsOrder } from '#components/OrderItemDetails';
import TitleBar, { TitleBarItemTraveler } from '#components/TitleBar';
import { FeedbackUIContext } from '#context/feedback-ui-context';

type MutationTrigger = (arg: Record<string, unknown>) => Promise<{ data?: unknown; error?: unknown }>;

interface InventoryItem {
  id?: string | number;
  product: { name?: string; vendor?: string; [key: string]: unknown };
  color?: string;
  size?: string;
  amounts?: number;
  [key: string]: unknown;
}

interface BarcodeItem {
  barcode: string;
  itemId?: string | number;
  received?: boolean;
  archived?: boolean;
  availableDate?: string | number | Date | null;
  orderId?: string | number;
  [key: string]: unknown;
}

interface InventoryOrder {
  id: string | number;
  _id?: string | number;
  fulfillmentStatus: number | string;
  items: InventoryItem[];
  barcodeItems: BarcodeItem[];
  inventoryOrderIndex?: number | undefined;
  total?: number | undefined;
  availableDate?: string | number | Date | null;
  createdDate?: string | number | Date;
  createdByUserName?: string;
  lastUpdatedBy?: string;
  notes?: string;
  hasRelatedOrders?: boolean;
  [key: string]: unknown;
}

interface DraftItem {
  _id?: string | number;
  data?: Record<string, unknown>;
}

export default function AdminInventoryOrderDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showBackdrop,
    hideBackdrop,

    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const [inventoryOrder, setInventoryOrder] = useState<InventoryOrder | undefined>();

  const [getInventoryOrderRaw] = useGetInventoryOrderMutation();
  const [updateInventoryOrderRaw] = useUpdateInventoryOrderMutation();
  const [receiveAllBarcodeItemsRaw] = useReceiveAllBarcodeItemsMutation();
  const getInventoryOrder = getInventoryOrderRaw as unknown as MutationTrigger;
  const updateInventoryOrder = updateInventoryOrderRaw as unknown as MutationTrigger;
  const receiveAllBarcodeItems = receiveAllBarcodeItemsRaw as unknown as MutationTrigger;
  const [diffs, setDiffs] = useState<Diff[]>([]);
  const [selected, setSelected] = useState<Array<string | number | undefined>>([]);
  const draftItem = useSelector(
    (state: { draftItem: { item: unknown } }) => state.draftItem.item,
  ) as DraftItem | null;

  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);

  useEffect(() => {
    showBackdrop();
    void getInventoryOrder(urlParams.values)
      .then((res) => {
        hideBackdrop();
        if (res.error) {
          return;
        }

        const data = res.data as InventoryOrder;
        const newOrder: InventoryOrder = { ...data };
        if (draftItem && data._id === draftItem._id) {
          const newItem: Record<string, unknown> = { ...draftItem.data };
          newItem._id = `unsaved_${makeid(5)}`;
          setDiffs([...diffs, {
            event: 'add',
            scope: 'items',
            data: newItem,
          }]);

          newOrder.items = [...newOrder.items, newItem as unknown as InventoryItem];
        }

        setInventoryOrder(newOrder);
      });
  }, [location.key]);

  if (!inventoryOrder) {
    return null;
  }

  const cancelled = (inventoryOrder.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED);

  const totalItemsCount = inventoryOrder.items.reduce((sum, item) => sum + (item.amounts || 0), 0);

  const subtotal = calcOrderTotalPrice({
    ...inventoryOrder,
  } as unknown as Parameters<typeof calcOrderTotalPrice>[0]);

  const orderProps: EnhancedTableData = {
    rows: inventoryOrder.items,
  };

  const receivingProps: EnhancedTableData = {
    rows: inventoryOrder.barcodeItems.map((bc) => ({
      ...bc,
      id: bc.barcode,
    })),

    hasSelectColumn: !cancelled,
    canSelect: (row) => {
      const r = row as unknown as BarcodeItem;
      return !r.received && !r.archived;
    },
    canSelectAll: (rows) => rows.some((row) => {
      const r = row as unknown as BarcodeItem;
      return !r.received && !r.archived;
    }),
    selected,
    setSelected,
  };

  const handleSaveOrders = () => {
    showBackdrop();

    void updateInventoryOrder({
      inventoryOrderId: inventoryOrder.id,
      diffs,
    }).then((res) => {
      const hasError = simpleSnackbar(
        res as unknown as Parameters<typeof simpleSnackbar>[0],
        'Inventory order has been saved.',
      );
      if (hasError) {
        return;
      }

      const newInventoryOrder: InventoryOrder = {
        inventoryOrderIndex: inventoryOrder.inventoryOrderIndex,
        total: inventoryOrder.total,
        ...(res.data as InventoryOrder),
      };
      setInventoryOrder(newInventoryOrder);
      setDiffs([]);
    }).finally(() => {
      hideBackdrop();
    });
  };

  const handleBatchReceive = () => {
    if (!selected.length) {
      return;
    }

    setDialogData({
      title: 'Confirmation',
      text: `Receive selected ${selected.length} items into receiving list?`,
      agree: 'Yes',
      disagree: 'No',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        showBackdrop();

        void receiveAllBarcodeItems({
          inventoryOrderId: inventoryOrder.id,
          barcodes: selected,
        }).then((res) => {
          hideDialog();

          const hasError = simpleSnackbar(
            res as unknown as Parameters<typeof simpleSnackbar>[0],
            'Selected barcode items have been received.',
          );
          if (hasError) {
            return;
          }

          const newInventoryOrder: InventoryOrder = {
            inventoryOrderIndex: inventoryOrder.inventoryOrderIndex,
            total: inventoryOrder.total,
            ...(res.data as InventoryOrder),
          };
          setInventoryOrder(newInventoryOrder);
          setSelected([]);
        }).finally(() => {
          hideBackdrop();
        });
      },
    });
    showDialog();
  };

  const handleTravelOrder = (direction: number) => {
    urlParams.set('inventoryOrderIndex', (val) => String((val as number) + direction));
    const search = urlParams.toString();
    void navigate({
      pathname: '/management/inventory/detail',
      search,
    });
  };

  return (
    <>
      <TitleBar
        title="Inventory Order Detail"
        hideActionButtons={!urlParams.values.inventoryOrderIndex}
      >
        <TitleBarItemTraveler
          onTravelItem={(direction) => handleTravelOrder(direction)}
          itemIndex={inventoryOrder.inventoryOrderIndex as number}
          itemSize={inventoryOrder.total as number}
        />
      </TitleBar>
      <Box sx={{
        mb: 1,
        display: 'flex',
        justifyContent: 'space-between',
      }}
      >
        <Box>
          Inventory Order #
          {inventoryOrder.id}
          {' '}
          (
          {totalItemsCount}
          {' '}
          {totalItemsCount === 1 ? 'item' : 'items'}
          )
        </Box>

        <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
          <Select
            size="small"
            value={inventoryOrder.fulfillmentStatus}
            disabled={cancelled && !diffs.length}
            onChange={(e: SelectChangeEvent<number | string>) => {
              const newValue = e.target.value;
              setDiffsHelper('fulfillmentStatus', newValue, diffs, setDiffs);
              setInventoryOrder({
                ...inventoryOrder,
                fulfillmentStatus: newValue,
              });
            }}
          >
            {
              FULFILLMENT_DATA
                .filter((fd) => {
                  if (fd.id !== FULFILLMENT_STATUS.CANCELED) {
                    return true;
                  }

                  // some barcode item already received
                  if (inventoryOrder.barcodeItems.some((bi) => bi.received)) {
                    return false;
                  }

                  // some barcode item has been added to the customize orders
                  if (inventoryOrder.hasRelatedOrders) {
                    return false;
                  }

                  return true;
                })
                .map((fst) => (
                  <MenuItem key={fst.id} value={fst.id}>{fst.text}</MenuItem>
                ))
            }
          </Select>
        </FormControl>
      </Box>
      <EnhancedTable
        name="order-list"
        headers={[{
          id: 'style',
          label: 'Style #',
          renderer: (item) => (item as unknown as InventoryItem).product.name,
        }, {
          id: 'color',
          label: 'Color',
          renderer: (item) => (item as unknown as InventoryItem).color,
        }, {
          id: 'size',
          label: 'Size',
          renderer: (item) => (item as unknown as InventoryItem).size,
        }, {
          id: 'amounts',
          label: 'Amounts',
          renderer: (item) => (item as unknown as InventoryItem).amounts,
        }]}
        {...orderProps}
      />
      <Box
        sx={{
          typography: 'subtitle2',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <b>Subtotal</b>
        :
        {' '}
        {formatCurrency(subtotal)}
      </Box>
      <Divider />
      <Box
        sx={{
          py: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderRadius: 1,
          }}
        >
          <Typography
            variant={'h7' as TypographyProps['variant']}
            gutterBottom
            sx={{
              display: 'block',
            }}
          >
            Receiving List
          </Typography>
          <Button
            variant="outlined"
            disabled={!selected.length}
            onClick={handleBatchReceive}
          >
            Receive
          </Button>
        </Box>

        <EnhancedTable
          name="receiving-list"
          headers={[{
            id: 'barcode',
            label: 'Barcode',
            renderer: (row) => (row as unknown as BarcodeItem).barcode,
          }, {
            id: '',
            label: 'Item',
            renderer: (row) => {
              const r = row as unknown as BarcodeItem;
              const item = inventoryOrder.items.find((i) => i.id === r.itemId);
              return item ? (
                <OrderItemDetails
                  item={item}
                  order={inventoryOrder as unknown as OrderItemDetailsOrder}
                  isInventory
                />
              ) : '-';
            },
          }, {
            id: 'vendor',
            label: 'Vendor',
            renderer: (row) => {
              const r = row as unknown as BarcodeItem;
              const item = inventoryOrder.items.find((i) => i.id === r.itemId);
              return item?.product?.vendor || '-';
            },
          }, {
            id: 'status',
            label: 'Status',
            renderer: (row) => {
              const item = row as unknown as BarcodeItem;
              const availableDate = item.availableDate ? new Date(item.availableDate) : null;
              if (item.archived) {
                return (<Chip label="Pulled out" color="error" />);
              }
              if (item.received) {
                return (<Chip label="Received" color="success" />);
              }
              if (!availableDate) {
                return (<Chip label="Pending" color="warning" />);
              }
              if (availableDate < new Date()) {
                return (<Chip label="Past Due" color="error" />);
              }
              return (<Chip label="In Production" color="info" />);
            },
          }, {
            id: 'order',
            label: 'Purchased In',
            renderer: (row) => {
              const r = row as unknown as BarcodeItem;
              if (r.orderId) {
                return (
                  <Link
                    href={`/management/order/detail?orderId=${r.orderId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      void navigate(`/management/order/detail?orderId=${r.orderId}`);
                    }}
                    sx={{ textDecoration: 'inherit' }}
                  >
                    {r.orderId}
                  </Link>
                );
              }
              return '-';
            },
          }]}
          {...receivingProps}
        />
      </Box>
      <Box
        sx={{
          py: 3,
        }}
      >
        <Typography
          variant={'h7' as TypographyProps['variant']}
          gutterBottom
          sx={{
            display: 'block',
          }}
        >
          Available Date
        </Typography>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
        >
          <DesktopDatePicker
            format={DATE_FORMAT_STRING}
            value={((inventoryOrder.availableDate === null)
              ? ''
              : new Date(inventoryOrder.availableDate as string | number | Date)) as unknown as Date}
            disabled={cancelled}
            onChange={(d) => {
              setDiffsHelper('availableDate', d, diffs, setDiffs);
              setInventoryOrder({
                ...inventoryOrder,
                availableDate: d,
              });
            }}
            sx={{ minWidth: 350 }}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
      </Box>
      <Paper
        sx={{
          px: 2,
          mt: 2,
          '& .MuiGrid-root': { mb: 2 },
        }}
      >
        <Box sx={{ typography: 'subtitle2' }}>
          <Grid container>
            <Grid size={4}>
              Created Date
            </Grid>
            <Grid size={8}>
              {formatDate(inventoryOrder.createdDate as string)}
            </Grid>

            <Grid size={4}>
              Placed By
            </Grid>
            <Grid size={8}>
              {inventoryOrder.createdByUserName || '-'}
            </Grid>

            <Grid size={4}>
              Last Updated By
            </Grid>
            <Grid size={8}>
              {inventoryOrder.lastUpdatedBy || '-'}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Typography variant={'h7' as TypographyProps['variant']} gutterBottom>
          Notes
        </Typography>
      </Box>
      <Box>
        <TextField
          multiline
          fullWidth
          rows={5}
          value={inventoryOrder.notes}
          disabled={cancelled}
          onChange={(event) => {
            const newValue = event.target.value;
            setDiffsHelper('notes', newValue, diffs, setDiffs);
            setInventoryOrder({
              ...inventoryOrder,
              notes: newValue,
            });
          }}
        />
      </Box>
      <Box>
        <Button
          variant="contained"
          disabled={!diffs.length}
          onClick={handleSaveOrders}
        >
          Save
        </Button>
      </Box>
    </>
  );
}
